import {
    ChatFunctionalityType,
    CommunicationKitType,
    EmployerEditSuppressionType,
    FetchState,
    PartnerSettingsResponse,
    PartnerSettingsState,
} from "@/shared/types";
import { adminPartnerSettingsGet, adminPartnerSettingsSet, employerPartnerSettingsGet } from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";

const pageSize = 20;

export const initialState: PartnerSettingsState = {
    fetchState: FetchState.Uninitialized,
    settings: {
        canEmployerUpdateCensus: true,
        canViewEmployerSsns: false,
        usersAreAutomaticallyRedirectedToWex: false,
        partnerUsesCustomClaims: false,
        employeeLogoutRedirectUrl: null,
        communicationKitType: CommunicationKitType.None,
        employerEditSuppressionType: EmployerEditSuppressionType.None,
        advisorEmailName: "",
        advisorEmailFrom: "",
        claimsOptIn: false,
        disableTalon: false,
        showEmployerSignupQuestion: false,
        consumerLiveChatEnabled: false,
        consumerInitiateCobrowseEnabled: false,
        chatFunctionalityType: ChatFunctionalityType.ChatBotOnly,
        isEnrollmentDisabled: false,
        dateStopEmployeeContribution: null,
        disableInvestmentActivity: false,
        resourceOptimizationEnabled: false
    }
};

export const usePartnerSettingStore = defineStore("partnerSetting", {
    state: (): PartnerSettingsState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setPartnerSettings (settings: PartnerSettingsResponse) {
            if (!settings) {
                settings = {
                    partnerId: 0,
                    canViewEmployerSsns: false,
                    usersAreAutomaticallyRedirectedToWex: false,
                    canEmployerUpdateCensus: true,
                    partnerUsesCustomClaims: false,
                    employeeLogoutRedirectUrl: null,
                    communicationKitType: CommunicationKitType.None,
                    employerEditSuppressionType: EmployerEditSuppressionType.None,
                    advisorEmailFrom: "",
                    advisorEmailName: "",
                    claimsOptIn: false,
                    disableTalon: false,
                    showEmployerSignupQuestion: false,
                    consumerLiveChatEnabled: false,
                    consumerInitiateCobrowseEnabled: false,
                    chatFunctionalityType: ChatFunctionalityType.ChatBotOnly,
                    isEnrollmentDisabled: false,
                    dateStopEmployeeContribution: null,
                    disableInvestmentActivity: false,
                    resourceOptimizationEnabled: false,
                };
            }

            this.settings = {
                canEmployerUpdateCensus: !!settings.canEmployerUpdateCensus,
                canViewEmployerSsns: !!settings.canViewEmployerSsns,
                usersAreAutomaticallyRedirectedToWex: !!settings.usersAreAutomaticallyRedirectedToWex,
                partnerUsesCustomClaims: !!settings.partnerUsesCustomClaims,
                employeeLogoutRedirectUrl: settings.employeeLogoutRedirectUrl,
                communicationKitType: settings.communicationKitType,
                employerEditSuppressionType: settings.employerEditSuppressionType,
                advisorEmailFrom: settings.advisorEmailFrom,
                advisorEmailName: settings.advisorEmailName,
                claimsOptIn: settings.claimsOptIn,
                disableTalon: settings.disableTalon,
                showEmployerSignupQuestion: settings.showEmployerSignupQuestion,
                consumerLiveChatEnabled: settings.consumerLiveChatEnabled,
                consumerInitiateCobrowseEnabled: settings.consumerInitiateCobrowseEnabled,
                chatFunctionalityType: settings.chatFunctionalityType,
                isEnrollmentDisabled: settings.isEnrollmentDisabled,
                dateStopEmployeeContribution: (settings.dateStopEmployeeContribution || null),
                disableInvestmentActivity: settings.disableInvestmentActivity,
                resourceOptimizationEnabled: settings.resourceOptimizationEnabled,
            };
        },
        loadPartnerSettings (partnerId: number) {
            return adminPartnerSettingsGet(partnerId)
                .then(response => this.setPartnerSettings(response.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        beginSetPartnerSettings (settings: PartnerSettingsResponse) {
            if (!settings || _.isFinite(settings.partnerId) === false) {
                return Promise.resolve();
            }
            return adminPartnerSettingsSet(settings.partnerId, settings)
                .then(_ => this.setPartnerSettings(settings))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadEmployerPartnerSettings (args: { partnerId: number, employerId: number }) {
            return employerPartnerSettingsGet(args.partnerId, args.employerId)
                .then(response => this.setPartnerSettings(response.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});